/*
  Разбиение списка на страницы на стороне кабинета (21.10.2026, замечание владельца:
  «пагинация и количество записей в любой список и таблицу»).

  Один хук и одна панель (`PageBar.jsx`) на весь кабинет: прежде у списков были свои
  «Показать ещё» с разными шагами, а у реестра - своя панель, и размер страницы нельзя
  было выбрать нигде. Теперь размер выбирается человеком из 10, 25, 50 и 100 и
  запоминается ОДИН на все списки: человеку, выбравшему сто строк, не нужно выбирать их
  заново в каждой таблице. Запоминание - удобство одного браузера, поэтому хранилище
  может не ответить (закрытое окно, запрет), и тогда берётся размер по умолчанию.

  Список с сервера, который сервер сам режет на страницы (реестр), этим хуком не
  разбивается: у него своя выборка по смещению, а панель та же.
*/

import { useState } from 'react'

export const PAGE_SIZES = [10, 25, 50, 100]
const STORAGE_KEY = 'arbitr-zhkh.page-size'

function storage() {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function storedPageSize(fallback) {
  try {
    const value = Number(storage()?.getItem(STORAGE_KEY))
    return PAGE_SIZES.includes(value) ? value : fallback
  } catch {
    return fallback
  }
}

export function rememberPageSize(size) {
  try {
    storage()?.setItem(STORAGE_KEY, String(size))
  } catch {
    // Не запомнилось - размер просто не переживёт перезагрузку страницы.
  }
}

/*
  `rows` - весь список (или `null`, пока он грузится). Возвращает строки текущей
  страницы и всё, что нужно панели. Номер страницы подрезается по длине списка: после
  фильтра или удаления последней строки страницы человек остаётся на последней
  существующей, а не на пустой.

  `resetKey` (необязательный, 23.10.2026, находка проверки) - отбор, от которого зависит
  список: поиск, переключатель, выбранный дом. Его смена возвращает на ПЕРВУЮ страницу.
  Подрезка выше этого не делает: на шестой странице набрали фамилию, список сжался до сорока
  строк - и человек видел «31-40 из 40», то есть конец найденного вместо начала.
*/
export default function usePaging(rows, initialSize = 10, resetKey = null) {
  const [size, setSize] = useState(() => storedPageSize(initialSize))
  const [page, setPage] = useState(0)
  const [seenReset, setSeenReset] = useState(resetKey)
  if (seenReset !== resetKey) {
    setSeenReset(resetKey)
    setPage(0)
  }
  const total = rows ? rows.length : 0
  const pages = Math.max(1, Math.ceil(total / size))
  const current = Math.min(page, pages - 1)

  const onSize = (value) => {
    const next = Number(value)
    rememberPageSize(next)
    setSize(next)
    setPage(0)
  }

  return {
    rows: rows ? rows.slice(current * size, (current + 1) * size) : rows,
    bar: { total, size, page: current, pages, onPage: setPage, onSize },
  }
}
